import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  enviarMensagemChatIaStream,
  listarConversasChatIa,
  detalharConversaChatIa
} from '../../../api/chatIa';
import { useMenuSidebar } from '../../../contexts/MenuSidebarContext';
import { initEcho, getEcho } from '../../../services/authentication/echo';
import { getConfigAmbienteDominio, getDadosUsuario } from '../../../services/authentication/auth';

// Distancia do fim a partir da qual o auto-scroll para de acompanhar: se o
// usuario subiu para reler algo, puxar a view de volta a cada delta tornaria a
// leitura impossivel.
const MARGEM_ACOMPANHAR_FIM = 120;

/**
 * Orquestra o Drawer do chat de IA: envio da mensagem com streaming por
 * WebSocket (Reverb), historico local da conversa aberta, e pedido de contexto
 * de tela ao iframe ativo via IA_SOLICITAR_CONTEXTO.
 *
 * O streaming e so experiencia de digitacao: a resposta HTTP continua sendo a
 * fonte da verdade e sobrescreve o texto acumulado ao chegar. Assim, se o
 * socket cair ou nenhum delta chegar, a mensagem aparece igual — apenas de
 * uma vez.
 *
 * O front NUNCA decide quais dados o usuario pode ver — so envia a mensagem e
 * o identificador da conversa; todo filtro de escopo acontece no backend
 * (App\Http\Middleware\AutenticacaoChatIa).
 */
const useChatIAHook = ({ chatIaOpen }) => {
  const [mensagens, setMensagens] = useState([]);
  const [textoEntrada, setTextoEntrada] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [uuidConversa, setUuidConversa] = useState(null);
  const [conversas, setConversas] = useState([]);
  // A lista de conversas recentes precisa de estado proprio: sem isso, "Nova
  // conversa" seria a unica forma de voltar a lista, e nao haveria como sair
  // da lista sem abrir uma conversa.
  const [mostrandoLista, setMostrandoLista] = useState(false);
  // Etapa corrente do turno ('pensando', 'consultando', ...), usada so para
  // dar sinal de vida enquanto nada foi escrito ainda.
  const [statusProcessamento, setStatusProcessamento] = useState(null);
  const { enviarMensagemEsteira, contextoTelaChatIa, user } = useMenuSidebar();
  const refFimMensagens = useRef(null);
  const refListaMensagens = useRef(null);
  // A decisão de acompanhar precisa ser tomada ANTES de a nova bolha aumentar
  // scrollHeight. Medir somente depois da renderização faz uma resposta longa
  // parecer que o usuário subiu para reler, e o scroll deixa de acontecer.
  const deveAcompanharFimRef = useRef(true);

  // O 'user' do contexto so e preenchido quando a UserSideBar monta, e o login
  // grava apenas cod_usuario/nome_usuario. Ler direto do storage cobre os dois
  // momentos; sem nome, a saudacao vira generica em vez de exibir "undefined".
  const nomeUsuario = useMemo(() => {
    const dados = getDadosUsuario();
    const nomeCompleto = user?.nome ?? dados?.nome ?? dados?.nome_usuario ?? null;

    // So o primeiro nome: a saudacao fica curta e cabe na largura do painel.
    return nomeCompleto ? String(nomeCompleto).trim().split(' ')[0] : null;
  }, [user]);
  const contextoTelaRef = useRef(contextoTelaChatIa);
  contextoTelaRef.current = contextoTelaChatIa;
  // Acumula os deltas fora do state: setState e assincrono e em lote, entao
  // somar sobre o valor anterior a cada pedaco perderia texto.
  const textoParcialRef = useRef('');

  // Assina o canal privado do proprio usuario. O backend so autoriza a
  // inscricao se o tenant e o UUID baterem com o do usuario autenticado
  // (routes/channels.php), entao um usuario nao consegue ouvir a conversa
  // de outro.
  const uuidUsuario = user?.uuid;

  useEffect(() => {
    if (!chatIaOpen || !uuidUsuario) {
      return undefined;
    }

    initEcho();
    const echo = getEcho();

    if (!echo) {
      return undefined;
    }

    const dominio = getConfigAmbienteDominio().replaceAll('.', '_');
    const canal = `chat_ia.${dominio}.${uuidUsuario}`;

    // stopListening antes de listen: echo.private() reaproveita um canal ja
    // existente, mas listen() ACUMULA handlers. Se este efeito rodar duas vezes
    // (StrictMode em dev, ou uma nova referencia de user), o mesmo delta seria
    // processado duas vezes e o texto apareceria duplicado ate a resposta HTTP
    // sobrescrever a bolha.
    echo.private(canal).stopListening('.chat-ia.delta');

    echo.private(canal).listen('.chat-ia.delta', ({ tipo, dados }) => {
      if (tipo === 'status') {
        // 'respondendo' e o sinal de que o texto comecou a sair: o indicador
        // sai de cena e quem mostra andamento passa a ser a propria resposta.
        setStatusProcessamento(dados?.etapa === 'respondendo' ? null : (dados ?? null));
        return;
      }

      if (tipo !== 'delta' || !dados?.texto) {
        return;
      }

      textoParcialRef.current += dados.texto;
      const parcial = textoParcialRef.current;

      setMensagens(anteriores => {
        const ultima = anteriores[anteriores.length - 1];

        // Enquanto a resposta chega, mantemos uma unica bolha marcada como
        // parcial e vamos reescrevendo o conteudo dela.
        if (ultima?.papel === 'assistant' && ultima.parcial) {
          return [...anteriores.slice(0, -1), { ...ultima, conteudo: parcial }];
        }

        return [...anteriores, { papel: 'assistant', conteudo: parcial, parcial: true }];
      });
    });

    return () => {
      echo.private(canal).stopListening('.chat-ia.delta');
      echo.leave(canal);
    };
    // Depende do uuid, nao do objeto user: uma nova referencia com o mesmo
    // usuario nao deve reassinar o canal.
  }, [chatIaOpen, uuidUsuario]);

  useEffect(() => {
    if (chatIaOpen) {
      enviarMensagemEsteira({ type: 'IA_SOLICITAR_CONTEXTO' });
    }
  }, [chatIaOpen, enviarMensagemEsteira]);

  // Acompanha o fim da conversa. Durante o streaming isto roda a cada delta,
  // por isso sem 'smooth': dezenas de animacoes concorrentes por segundo se
  // atropelam e produzem tremor em vez de rolagem. useLayoutEffect roda após
  // o React gravar a bolha no DOM e antes de o navegador pintar o frame.
  useLayoutEffect(() => {
    const lista = refListaMensagens.current;

    if (!lista || !deveAcompanharFimRef.current) {
      return;
    }

    lista.scrollTo({ top: lista.scrollHeight, behavior: 'auto' });
  }, [mensagens, enviando]);

  const aoRolarMensagens = useCallback(() => {
    const lista = refListaMensagens.current;
    if (!lista) return;

    const distanciaDoFim = lista.scrollHeight - lista.scrollTop - lista.clientHeight;
    deveAcompanharFimRef.current = distanciaDoFim < MARGEM_ACOMPANHAR_FIM;
  }, []);

  useEffect(() => {
    if (chatIaOpen) {
      deveAcompanharFimRef.current = true;
    }
  }, [chatIaOpen]);

  const carregarConversas = useCallback(() => {
    listarConversasChatIa()
      .then(res => setConversas(res.data?.data ?? []))
      .catch(() => setConversas([]));
  }, []);

  useEffect(() => {
    if (chatIaOpen) {
      carregarConversas();
    }
  }, [chatIaOpen, carregarConversas]);

  const abrirConversa = useCallback(uuid => {
    detalharConversaChatIa(uuid)
      .then(res => {
        const conversa = res.data?.data;
        if (!conversa) return;
        setUuidConversa(conversa.uuid);
        setMostrandoLista(false);
        // Os cartoes e graficos daquele turno vem persistidos junto da
        // mensagem, entao a conversa reabre como estava -- e nao so com o
        // texto, como antes.
        setMensagens(
          (conversa.mensagens ?? []).map(mensagem => ({
            papel: mensagem.papel,
            conteudo: mensagem.conteudo,
            telas: mensagem.telas ?? [],
            graficos: mensagem.graficos ?? []
          }))
        );
      })
      .catch(() => setErro('Não foi possível carregar esta conversa.'));
  }, []);

  const novaConversa = useCallback(() => {
    setUuidConversa(null);
    setMensagens([]);
    setErro(null);
    setMostrandoLista(false);
    setTextoEntrada('');
  }, []);

  const alternarLista = useCallback(() => {
    setMostrandoLista(atual => !atual);
  }, []);

  const enviarMensagem = useCallback(
    textoExplicito => {
      // Guarda de tipo, nao '??': onClick={enviarMensagem} passaria o
      // SyntheticEvent como primeiro argumento, e um evento nao e nulo -- o
      // fallback nunca dispararia e o .trim() abaixo quebraria.
      const bruto = typeof textoExplicito === 'string' ? textoExplicito : textoEntrada;
      const texto = bruto.trim();

      if (!texto || enviando) {
        return;
      }

      setErro(null);
      setEnviando(true);
      setTextoEntrada('');
      textoParcialRef.current = '';
      setStatusProcessamento({ etapa: 'pensando' });
      setMensagens(anteriores => [...anteriores, { papel: 'user', conteudo: texto }]);

      const codClienteSelecionado = contextoTelaRef.current?.identificadores?.codCliente ?? null;

      enviarMensagemChatIaStream({
        mensagem: texto,
        uuidConversa,
        codClienteSelecionado
      })
        .then(res => {
          const resultado = res.data?.data;
          setUuidConversa(resultado?.uuid_conversa ?? uuidConversa);
          setMensagens(anteriores => {
            const ultima = anteriores[anteriores.length - 1];
            const definitiva = {
              papel: 'assistant',
              conteudo: resultado?.resposta ?? '',
              // As telas vem em 'meta' porque nao fazem parte do turno em si:
              // sao o que a interface usa para montar os cartoes. Ficam presas
              // a ESTA mensagem para os cartoes de um turno nunca aparecerem
              // sob a resposta de outro.
              telas: res.data?.meta?.telas_referenciadas ?? [],
              // Mesma razao das telas: os graficos sao presos a ESTA mensagem
              // para nao aparecerem sob a resposta de outro turno. A serie vem
              // montada do backend a partir do resultado real da consulta.
              graficos: res.data?.meta?.graficos ?? []
            };

            // A resposta HTTP e a fonte da verdade: substitui a bolha parcial
            // montada pelos deltas, garantindo o texto final mesmo se algum
            // pedaco tiver se perdido no caminho.
            return ultima?.papel === 'assistant' && ultima.parcial
              ? [...anteriores.slice(0, -1), definitiva]
              : [...anteriores, definitiva];
          });
          carregarConversas();
        })
        .catch(err => {
          const mensagemErro =
            err?.response?.data?.message ?? 'Não foi possível obter resposta no momento.';
          setErro(mensagemErro);
          setMensagens(anteriores => {
            const ultima = anteriores[anteriores.length - 1];
            const bolhaErro = { papel: 'assistant', conteudo: mensagemErro, comErro: true };

            // Descarta o parcial: um texto interrompido no meio confundiria
            // mais do que a propria mensagem de erro.
            return ultima?.papel === 'assistant' && ultima.parcial
              ? [...anteriores.slice(0, -1), bolhaErro]
              : [...anteriores, bolhaErro];
          });
        })
        .finally(() => {
          textoParcialRef.current = '';
          setStatusProcessamento(null);
          setEnviando(false);
        });
    },
    [textoEntrada, enviando, uuidConversa, carregarConversas]
  );

  return {
    mensagens,
    textoEntrada,
    setTextoEntrada,
    enviando,
    erro,
    conversas,
    uuidConversa,
    mostrandoLista,
    statusProcessamento,
    nomeUsuario,
    refFimMensagens,
    refListaMensagens,
    aoRolarMensagens,
    alternarLista,
    abrirConversa,
    novaConversa,
    enviarMensagem
  };
};

export default useChatIAHook;
